// Validadores específicos para usuarios
import { isEmail, isPhone, isNotEmpty, hasMinLength, hasMaxLength, isAlpha } from './common';
import type { UserWizardData } from '../../types/user.types';

export const validateUserEmail = (
  email: string | null | undefined,
  existingEmails?: string[],
  currentEmail?: string,
): string | null => {
  if (!isNotEmpty(email)) {
    return 'El correo es requerido';
  }

  const normalizedEmail = email!.trim().toLowerCase();

  if (!isEmail(normalizedEmail)) {
    return 'El correo no tiene un formato válido';
  }

  if (!hasMaxLength(normalizedEmail, 255)) {
    return 'El correo no puede exceder 255 caracteres';
  }

  if (existingEmails && existingEmails.length > 0) {
    const normalizedCurrent = currentEmail?.trim().toLowerCase();
    const exists = existingEmails
      .map((item) => item.trim().toLowerCase())
      .includes(normalizedEmail);

    if (exists && normalizedEmail !== normalizedCurrent) {
      return 'El correo ya está registrado';
    }
  }

  return null;
};

export const validateUserPassword = (
  password: string | null | undefined,
  isEditMode: boolean = false,
): string | null => {
  if (!isNotEmpty(password)) {
    return isEditMode ? null : 'La contraseña es requerida';
  }

  const value = password!.trim();

  if (!hasMinLength(value, 8)) {
    return 'La contraseña debe tener al menos 8 caracteres';
  }

  if (!hasMaxLength(value, 128)) {
    return 'La contraseña no puede exceder 128 caracteres';
  }

  if (!/[A-Z]/.test(value)) {
    return 'La contraseña debe incluir al menos una letra mayúscula';
  }

  if (!/[0-9]/.test(value)) {
    return 'La contraseña debe incluir al menos un número';
  }

  return null;
};

export const validateUserPhone = (phone: string | null | undefined): string | null => {
  if (!isNotEmpty(phone)) {
    return null;
  }

  if (!isPhone(phone!)) {
    return 'El teléfono no tiene un formato válido';
  }

  return null;
};

export const validateUserName = (
  name: string | null | undefined,
  fieldName: 'firstName' | 'lastName',
): string | null => {
  const label = fieldName === 'firstName' ? 'nombre' : 'apellido';

  if (!isNotEmpty(name)) {
    return `El ${label} es requerido`;
  }

  if (!hasMinLength(name!, 2)) {
    return `El ${label} debe tener al menos 2 caracteres`;
  }

  if (!hasMaxLength(name!, 50)) {
    return `El ${label} no puede exceder 50 caracteres`;
  }

  if (!isAlpha(name!)) {
    return `El ${label} solo puede contener letras y espacios`;
  }

  return null;
};

export const validateUserRoles = (roleIds: string[] | null | undefined): string | null => {
  if (!roleIds || roleIds.length === 0) {
    return 'Debe asignar al menos un rol';
  }

  return null;
};

export const validateUserPrimaryLocation = (
  primaryLocationId: number | null | undefined,
): string | null => {
  if (!primaryLocationId) {
    return 'Debe asignar una sucursal';
  }
  return null;
};

export const validateUserForm = (
  formData: UserWizardData,
  existingEmails?: string[],
  currentEmail?: string,
  _isEditMode: boolean = false,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  const emailError = validateUserEmail(formData.email || '', existingEmails, currentEmail);
  if (emailError) errors.email = emailError;

  const firstNameError = validateUserName(formData.firstName || '', 'firstName');
  if (firstNameError) errors.firstName = firstNameError;

  const lastNameError = validateUserName(formData.lastName || '', 'lastName');
  if (lastNameError) errors.lastName = lastNameError;

  // phone es opcional en UserWizardData
  const phoneError = validateUserPhone(formData.phone || '');
  if (phoneError) errors.phone = phoneError;

  const rolesError = validateUserRoles(formData.roleIds || []);
  if (rolesError) errors.roleIds = rolesError;

  const locationError = validateUserPrimaryLocation(formData.primaryLocationId);
  if (locationError) errors.primaryLocationId = locationError;

  return errors;
};
